/**
 * Game settings — persistent, independent of game save state.
 *
 * Lives in its own localStorage key so it survives "Reset farm" (which
 * clears the game save). Add new settings here as the panel grows;
 * each one is a typed field + getter / setter + persistence.
 *
 * Pattern:
 *   const settings = new Settings();
 *   sounds.connectSettings(settings);     // subscriber wires itself
 *   diorama.connectSettings(settings);
 *   ...
 *   settings.setMasterVolume(0.7);        // notifies all subscribers
 *
 * Subscribers receive a notification on ANY change. The cost of
 * re-applying a few values per change is trivial vs the complexity of
 * per-field subscriptions.
 */

const KEY = 'sky-farm-settings@v1';

/** Graphics fidelity preset. Each preset bundles a set of graphics
 *  flags (render scale, shadows, post effects, particles, etc.); the
 *  player can pick a preset for one-click quality and the panel can
 *  then expose individual toggles for fine-tuning. The moment the
 *  player flips an individual toggle the active preset becomes
 *  `'custom'` so the chip selection reflects that they're off-preset. */
export type GraphicsPreset = 'low' | 'medium' | 'high' | 'ultra' | 'custom';

export interface SettingsData {
  /** Master audio level — slider value 0..1.
   *  At 1.0 the audio bus runs at the engine's calibrated default
   *  (SoundBank's master gain 0.5). Lower values quiet everything
   *  proportionally; 0 silences. */
  masterVolume: number;
  /** Hard mute toggle — overrides masterVolume. When true the audio
   *  bus is silenced regardless of the slider position. */
  muted: boolean;

  // ── Graphics ─────────────────────────────────────────────────────
  /** Currently selected preset — drives the chip highlight in the
   *  Graphics section. Set to `'custom'` when individual toggles
   *  diverge from the preset that was last applied. */
  graphicsPreset: GraphicsPreset;
  /** Render scale (renderer.pixelRatio). 0.5 = quarter pixel count
   *  (huge speed-up on weak GPUs); 1.0 = native; 1.5-2.0 = retina.
   *  Capped at the device's window.devicePixelRatio at apply-time. */
  renderScale: number;
  /** Sun-cast shadows on/off. Off saves a full shadow-map pass per
   *  frame at the cost of grounding cues. */
  shadows: boolean;
  /** UnrealBloom — sun-lit roofs and bright crops glow at golden hour.
   *  Internal pass count is high; disable for big perf wins. */
  bloom: boolean;
  /** Tilt-shift miniature blur on the top/bottom of the frame. */
  tiltShift: boolean;
  /** Warm grade + vignette pass — atmospheric tinting and dark corners. */
  vignette: boolean;
  /** Camera-shake kicks on chops / placements / errands. Disabling
   *  helps motion-sensitive players. */
  cameraShake: boolean;
  /** Seasonal weather particles (falling leaves in fall, snow in
   *  winter, pollen drift in spring, heat shimmer in summer). */
  seasonalFx: boolean;
  /** Firefly glow during dusk → night. */
  fireflies: boolean;

  // ── UI ───────────────────────────────────────────────────────────
  /** Show the FPS / perf chip at the bottom of the screen. */
  showFps: boolean;
}

/** Default = `'high'` preset values (matches the previous always-on
 *  behaviour, so existing players' experience is unchanged on first
 *  load). showFps and audio defaults are unrelated to graphics
 *  quality so they aren't part of any preset. */
const DEFAULTS: SettingsData = {
  masterVolume: 1.0,
  muted: false,
  graphicsPreset: 'high',
  renderScale: 1.0,
  shadows: true,
  bloom: true,
  tiltShift: true,
  vignette: true,
  cameraShake: true,
  seasonalFx: true,
  fireflies: true,
  showFps: true,
};

/** Maximum effective gain that maps to slider value 1.0. Matches
 *  SoundBank's default master gain, so "100% in settings" = "the
 *  level the audio was tuned at". Bumping this would let players
 *  push the SFX bus louder than the original calibration. */
export const MAX_MASTER_GAIN = 0.5;

/** Per-preset graphics flag values. The `custom` preset is computed
 *  on the fly (any individual toggle flips the preset to custom) so
 *  it's not represented here. */
type PresetFields = Pick<SettingsData,
  | 'renderScale' | 'shadows' | 'bloom' | 'tiltShift' | 'vignette'
  | 'cameraShake' | 'seasonalFx' | 'fireflies'>;

const PRESETS: Record<Exclude<GraphicsPreset, 'custom'>, PresetFields> = {
  // Aggressive cuts — lowest possible cost. Aimed at integrated GPUs
  // or older laptops; the diorama still reads clearly via the warm
  // grade pass (which we always keep on) but loses the atmospheric
  // sugar. 0.75 px ratio chosen over 0.5 because the latter pushes
  // pixel-art voxels into mushy territory.
  low: {
    renderScale: 0.75,
    shadows: false,
    bloom: false,
    tiltShift: false,
    vignette: false,
    cameraShake: false,
    seasonalFx: false,
    fireflies: false,
  },
  // Balanced — native resolution + shadows for grounding, drops the
  // GPU-heavy post effects. Best default for a modern integrated GPU.
  medium: {
    renderScale: 1.0,
    shadows: true,
    bloom: false,
    tiltShift: false,
    vignette: true,
    cameraShake: true,
    seasonalFx: true,
    fireflies: false,
  },
  // The originally-shipped look — full post stack at native res.
  high: {
    renderScale: 1.0,
    shadows: true,
    bloom: true,
    tiltShift: true,
    vignette: true,
    cameraShake: true,
    seasonalFx: true,
    fireflies: true,
  },
  // For high-DPI displays — runs the composer chain at full retina.
  // `applyGraphicsPreset` caps `renderScale` to the device's
  // devicePixelRatio at apply time so we don't supersample beyond
  // physical pixels.
  ultra: {
    renderScale: 2.0,
    shadows: true,
    bloom: true,
    tiltShift: true,
    vignette: true,
    cameraShake: true,
    seasonalFx: true,
    fireflies: true,
  },
};

export class Settings {
  private data: SettingsData = { ...DEFAULTS };
  private listeners = new Set<() => void>();

  constructor() {
    this.load();
  }

  // ── Reads ──────────────────────────────────────────────────────────
  getMasterVolume() { return this.data.masterVolume; }
  isMuted()         { return this.data.muted; }
  showFps()         { return this.data.showFps; }
  seasonalFx()      { return this.data.seasonalFx; }
  fireflies()       { return this.data.fireflies; }
  shadows()         { return this.data.shadows; }
  bloom()           { return this.data.bloom; }
  tiltShift()       { return this.data.tiltShift; }
  vignette()        { return this.data.vignette; }
  cameraShake()     { return this.data.cameraShake; }
  renderScale()     { return this.data.renderScale; }
  graphicsPreset()  { return this.data.graphicsPreset; }

  /** Effective audio bus gain — what should be applied to SoundBank.
   *  Folds in mute + slider mapping in one place so subscribers don't
   *  duplicate the math. */
  effectiveBusGain(): number {
    return this.data.muted ? 0 : this.data.masterVolume * MAX_MASTER_GAIN;
  }

  // ── Writes ─────────────────────────────────────────────────────────
  setMasterVolume(v: number) {
    this.data.masterVolume = clamp01(v);
    this.commit();
  }
  setMuted(b: boolean)      { this.data.muted = !!b;      this.commit(); }
  toggleMute()              { this.setMuted(!this.data.muted); }
  setShowFps(b: boolean)    { this.data.showFps = !!b;    this.commit(); }

  /** Graphics toggles — each flips the preset to `'custom'` since
   *  the individual change diverges from whatever preset was active. */
  setSeasonalFx(b: boolean) { this.data.seasonalFx = !!b; this.markCustom(); this.commit(); }
  setFireflies(b: boolean)  { this.data.fireflies = !!b;  this.markCustom(); this.commit(); }
  setShadows(b: boolean)    { this.data.shadows = !!b;    this.markCustom(); this.commit(); }
  setBloom(b: boolean)      { this.data.bloom = !!b;      this.markCustom(); this.commit(); }
  setTiltShift(b: boolean)  { this.data.tiltShift = !!b;  this.markCustom(); this.commit(); }
  setVignette(b: boolean)   { this.data.vignette = !!b;   this.markCustom(); this.commit(); }
  setCameraShake(b: boolean){ this.data.cameraShake = !!b;this.markCustom(); this.commit(); }
  setRenderScale(v: number) {
    // Cap at the device's DPR so we never request supersampling beyond
    // physical pixels (wasted GPU). Clamped above 0.25 to keep the
    // image at least vaguely recognizable.
    const dpr = typeof window !== 'undefined' && window.devicePixelRatio
      ? window.devicePixelRatio : 1;
    this.data.renderScale = clamp(v, 0.25, Math.max(1, dpr));
    this.markCustom();
    this.commit();
  }

  /** Snap all graphics fields to the named preset. Idempotent — applying
   *  the same preset twice in a row is a no-op for subscribers. */
  applyGraphicsPreset(p: Exclude<GraphicsPreset, 'custom'>) {
    const cfg = PRESETS[p];
    // Cap renderScale at the device's DPR (ultra requests 2.0 but a
    // 96-DPI desktop only has 1.0 physical pixels).
    const dpr = typeof window !== 'undefined' && window.devicePixelRatio
      ? window.devicePixelRatio : 1;
    this.data.renderScale = Math.min(cfg.renderScale, Math.max(1, dpr));
    this.data.shadows = cfg.shadows;
    this.data.bloom = cfg.bloom;
    this.data.tiltShift = cfg.tiltShift;
    this.data.vignette = cfg.vignette;
    this.data.cameraShake = cfg.cameraShake;
    this.data.seasonalFx = cfg.seasonalFx;
    this.data.fireflies = cfg.fireflies;
    this.data.graphicsPreset = p;
    this.commit();
  }

  /** Restore all settings to their defaults. */
  resetToDefaults() {
    this.data = { ...DEFAULTS };
    this.commit();
  }

  // ── Subscription ──────────────────────────────────────────────────
  /** Register a change listener. Returns an unsubscribe function. */
  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => { this.listeners.delete(fn); };
  }

  // ── Internals ─────────────────────────────────────────────────────
  /** Called by individual graphics setters — flags the preset state
   *  as custom unless the new combination happens to match a preset
   *  exactly (which lets the chip "snap back" to a real preset if the
   *  player manually toggles everything to match one). */
  private markCustom() {
    const match = this.matchPreset();
    this.data.graphicsPreset = match ?? 'custom';
  }

  /** Detect whether the current graphics fields match any named
   *  preset exactly. Returns the preset name or null. */
  private matchPreset(): Exclude<GraphicsPreset, 'custom'> | null {
    const dpr = typeof window !== 'undefined' && window.devicePixelRatio
      ? window.devicePixelRatio : 1;
    for (const name of Object.keys(PRESETS) as Array<Exclude<GraphicsPreset, 'custom'>>) {
      const p = PRESETS[name];
      const effectiveRs = Math.min(p.renderScale, Math.max(1, dpr));
      if (
        Math.abs(this.data.renderScale - effectiveRs) < 1e-3 &&
        this.data.shadows === p.shadows &&
        this.data.bloom === p.bloom &&
        this.data.tiltShift === p.tiltShift &&
        this.data.vignette === p.vignette &&
        this.data.cameraShake === p.cameraShake &&
        this.data.seasonalFx === p.seasonalFx &&
        this.data.fireflies === p.fireflies
      ) {
        return name;
      }
    }
    return null;
  }

  private commit() {
    this.persist();
    for (const fn of this.listeners) fn();
  }

  private load() {
    try {
      const raw = localStorage.getItem(KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Partial<SettingsData>;
      // Merge field-by-field so a missing key in storage falls back
      // to the default — important when new settings are added in
      // future versions and existing players haven't seen them.
      if (typeof parsed.masterVolume === 'number') this.data.masterVolume = clamp01(parsed.masterVolume);
      if (typeof parsed.muted === 'boolean')       this.data.muted = parsed.muted;
      if (typeof parsed.showFps === 'boolean')     this.data.showFps = parsed.showFps;
      if (typeof parsed.seasonalFx === 'boolean')  this.data.seasonalFx = parsed.seasonalFx;
      if (typeof parsed.fireflies === 'boolean')   this.data.fireflies = parsed.fireflies;
      if (typeof parsed.shadows === 'boolean')     this.data.shadows = parsed.shadows;
      if (typeof parsed.bloom === 'boolean')       this.data.bloom = parsed.bloom;
      if (typeof parsed.tiltShift === 'boolean')   this.data.tiltShift = parsed.tiltShift;
      if (typeof parsed.vignette === 'boolean')    this.data.vignette = parsed.vignette;
      if (typeof parsed.cameraShake === 'boolean') this.data.cameraShake = parsed.cameraShake;
      if (typeof parsed.renderScale === 'number')  this.data.renderScale = clamp(parsed.renderScale, 0.25, 4);
      if (typeof parsed.graphicsPreset === 'string' &&
          ['low','medium','high','ultra','custom'].includes(parsed.graphicsPreset)) {
        this.data.graphicsPreset = parsed.graphicsPreset as GraphicsPreset;
      }
      // After loading, if the saved preset name doesn't actually match
      // the saved flag combination (e.g. user manually edited storage,
      // or schema drifted), recompute it from the flags so the chip
      // highlight is accurate.
      const detected = this.matchPreset();
      if (detected) this.data.graphicsPreset = detected;
      else if (this.data.graphicsPreset !== 'custom') this.data.graphicsPreset = 'custom';
    } catch {
      /* corrupt / missing — defaults stay */
    }
  }

  private persist() {
    try { localStorage.setItem(KEY, JSON.stringify(this.data)); }
    catch (err) { console.warn('Settings persist failed', err); }
  }
}

function clamp01(v: number): number {
  if (typeof v !== 'number' || !isFinite(v)) return 0;
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
function clamp(v: number, lo: number, hi: number): number {
  if (typeof v !== 'number' || !isFinite(v)) return lo;
  return v < lo ? lo : v > hi ? hi : v;
}
